/**
 * DriftTableWithBulkActions — the DSC drift queue as Carbon's DataTable
 * (components/carbon/DataTable, mockup 02): the status tabs the parent owns,
 * a toolbar with the Severity select, the filter menu and the audit export,
 * the Soft Green batch bar while rows are selected, resources with several
 * drifted settings grouped under one header, rows that expand in place
 * (`renderDetail`, D25), and Carbon pagination.
 *
 * Data stays server-side: severity, status and dates filter the query in
 * DriftManagementTab, and the table pages 20 rows at a time. Bulk status
 * changes post here and ask the parent to refresh.
 */

import { useState, type ReactNode } from 'react'
import type { ConfigurationDrift, DriftSeverity, DriftStatus } from '@/types'
import apiClient from '@/lib/api'
import toast from 'react-hot-toast'
import { workloadLabelForResourceType } from '@/lib/tcmResourceNames'
import { driftResourceName, dscResourceLabel, settingLabel } from '@/lib/drift-names'
import RejectionNotificationModal from '@/components/RejectionNotificationModal'
import BulkActionCommentModal from '@/components/Drift/BulkActionCommentModal'
import DriftRefinePopover, { type DriftFilterValues, type HiddenStatuses } from '@/components/Drift/DriftFilters'
import TriagePill from '@/components/Drift/TriagePill'
import { Button } from '@/components/carbon/Button'
import { DataTable, TableItem, type DataColumn, type DataTableGroups } from '@/components/carbon/DataTable'
import type { BatchAction } from '@/components/carbon/BatchActionBar'
import { IconButton } from '@/components/carbon/IconButton'
import { IconCheck, IconClose, IconDownload } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { severityFromApi } from '@/components/carbon/severity'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { TableSelect } from '@/components/carbon/TableSelect'
import { Tag } from '@/components/carbon/Tag'
import { ChangeCell, RowToggleTitle, WhenCell } from '@/components/BaselineCompliance/drift-carbon'
import {
  QUEUE_NOT_ASSESSED,
  severityFilterOptions,
  toggleId,
  type SeverityCounts,
} from '@/components/BaselineCompliance/drift-carbon-helpers'

/* ────────────────────────────────────────────────────────────────────
   Types & local maps
   ──────────────────────────────────────────────────────────────────── */

const severityOrder: Record<DriftSeverity, number> = {
  Critical: 4,
  High: 3,
  Medium: 2,
  Low: 1,
}

/** The resource a drift belongs to (component:componentName): its group in the table. */
function groupKey(drift: ConfigurationDrift): string {
  return `${drift.component}:${drift.componentName || drift.component}`
}

function formatStatusLabel(status: string): string {
  switch (status) {
    case 'PendingApproval':
      return 'Pending'
    case 'Approved':
      return 'Accepted'
    case 'AwaitingRemediation':
      return 'Awaiting'
    default:
      return status.replace(/([A-Z])/g, ' $1').trim()
  }
}

/* ────────────────────────────────────────────────────────────────────
   Small presentational primitives (shared with the drift detail)
   ──────────────────────────────────────────────────────────────────── */

/** Severity as Carbon's glyph and word (the Shape-First Severity Rule): only Critical takes a hue. */
export function SevPill({ severity }: { severity: DriftSeverity }) {
  return <SeverityGlyph level={severityFromApi(severity)}>{severity}</SeverityGlyph>
}

/** A drift's pipeline status as a default (neutral) Carbon tag. */
export function StatusPill({ status }: { status: string }) {
  return <Tag className="tabular-nums">{formatStatusLabel(status)}</Tag>
}

/** The When cell's status tag: none for a New drift, nor for Remediated in the Back to baseline view (the tab says it). */
function lifecycleTag(status: string, archiveMode: boolean): string | null {
  if (status === 'New' || (archiveMode && status === 'Remediated')) return null
  return formatStatusLabel(status)
}

/* ────────────────────────────────────────────────────────────────────
   Batch verbs
   ──────────────────────────────────────────────────────────────────── */

interface VerbButton {
  key: string
  label: string
  shortLabel?: string
  status: DriftStatus
}

// The two decisions an operator reaches for most lead the batch bar. The
// status VALUES are unchanged from the original six-verb list so the existing
// bulk handlers keep working untouched.
const PRIMARY_VERBS: ReadonlyArray<VerbButton> = [
  { key: 'approve', label: 'Accept as new baseline', shortLabel: 'Accept', status: 'Approved' as DriftStatus },
  { key: 'reject', label: 'Reject', status: 'Rejected' as DriftStatus },
]

// The remaining lifecycle verbs live in the batch bar's "More actions" menu.
const OVERFLOW_VERBS: ReadonlyArray<VerbButton> = [
  { key: 'awaiting', label: 'Awaiting remediation', shortLabel: 'Awaiting', status: 'AwaitingRemediation' as DriftStatus },
  { key: 'remediated', label: 'Mark remediated', shortLabel: 'Remediated', status: 'Remediated' as DriftStatus },
  { key: 'new', label: 'Mark new', shortLabel: 'New', status: 'New' as DriftStatus },
  { key: 'ignore', label: 'Ignore', status: 'Ignored' as DriftStatus },
]

/* ────────────────────────────────────────────────────────────────────
   Main component
   ──────────────────────────────────────────────────────────────────── */

interface Props {
  drifts: ConfigurationDrift[]
  isLoading: boolean
  onRowClick: (id: string) => void
  selectedId: string | null
  pagination: {
    current: number
    total: number
    pageSize: number
    onPageChange: (page: number) => void
  }
  onRefresh: () => void
  filters: DriftFilterValues
  onFilterChange: (filters: DriftFilterValues) => void
  severityCounts?: SeverityCounts
  /** Nothing scanned or the stats call failed: the Severity filter shows no counts rather than zeros. */
  countsUnknown?: boolean
  /** Nothing scanned: the empty, unfiltered Open view says there is no baseline data yet. */
  unscanned?: boolean
  archiveMode?: boolean
  /**
   * True for a Viewer (`canChangeDrifts`): no selection checkboxes, no bulk
   * bar and no "Cancel pending". The server 403s these writes below Approver.
   */
  readOnly?: boolean
  /**
   * The in-place detail of an expanded row (D25). When set, rows expand in
   * place; without it a row click calls `onRowClick` (the legacy drawer).
   */
  renderDetail?: (drift: ConfigurationDrift) => ReactNode
  /** The status tabs above the toolbar (ContainedTabs), owned by the parent. */
  tabs?: ReactNode
  /** Adds the toolbar's "Export audit" icon button. */
  onExport?: () => void
  /** The statuses the open view leaves out, as a "Hide" group in the filter menu. */
  hiddenStatuses?: HiddenStatuses
}

export default function DriftTableWithBulkActions({
  drifts,
  isLoading,
  onRowClick,
  selectedId: _selectedId,
  pagination,
  onRefresh,
  filters,
  onFilterChange,
  severityCounts,
  countsUnknown = false,
  unscanned = false,
  archiveMode = false,
  readOnly = false,
  renderDetail,
  tabs,
  onExport,
  hiddenStatuses,
}: Props) {
  const [selectedDrifts, setSelectedDrifts] = useState<Set<string>>(new Set())
  const [bulkActionInProgress, setBulkActionInProgress] = useState(false)
  const [showImpactWarning, setShowImpactWarning] = useState(false)
  const [bulkApprovalImpact, setBulkApprovalImpact] = useState<any>(null)
  const [showRejectionNotification, setShowRejectionNotification] = useState(false)
  const [rejectedDriftCount, setRejectedDriftCount] = useState(0)
  const [showCommentModal, setShowCommentModal] = useState(false)
  const [pendingBulkAction, setPendingBulkAction] = useState<DriftStatus | null>(null)
  // Controlled row expansion, so a row's title toggles its detail as the chevron does.
  const [expandedIds, setExpandedIds] = useState<string[]>([])

  const pendingDrifts = drifts.filter(
    (drift) => drift.status === 'Pending' || drift.status === 'PendingApproval',
  )
  const hasPendingDrifts = pendingDrifts.length > 0

  const handleCancelPendingApprovals = async () => {
    const confirmed = window.confirm(
      `Cancel ${pendingDrifts.length} pending approval(s) and reset them to New status?`,
    )
    if (!confirmed) return

    setBulkActionInProgress(true)
    try {
      const pendingDriftIds = pendingDrifts.map((d) => d.id)

      await apiClient.post('/configurationdrift/bulk-status-change', {
        driftIds: pendingDriftIds,
        newStatus: 'New',
        approvedBy: 'Current User',
        comments: 'Cancelled pending approval - reset to New',
      })

      toast.success(`Cancelled ${pendingDriftIds.length} pending approvals.`)
      onRefresh()
    } catch (error: any) {
      console.error('Cancel pending approvals error:', error)
      toast.error(error.response?.data?.error || 'Failed to cancel pending approvals')
    } finally {
      setBulkActionInProgress(false)
    }
  }

  const handleBulkStatusChange = async (newStatus: DriftStatus) => {
    if (selectedDrifts.size === 0) {
      toast.error('No drifts selected')
      return
    }

    if (newStatus === 'Approved' && !showImpactWarning) {
      setBulkActionInProgress(true)
      try {
        const impactResponse = await apiClient.post('/configurationdrift/bulk-approval-impact', {
          driftIds: Array.from(selectedDrifts),
        })

        if (impactResponse.data.hasResourceLevelUpdates) {
          setBulkApprovalImpact(impactResponse.data)
          setShowImpactWarning(true)
          setBulkActionInProgress(false)
          return
        }
      } catch (error) {
        console.error('Failed to check bulk approval impact:', error)
      }
      setBulkActionInProgress(false)
    }

    setPendingBulkAction(newStatus)
    setShowCommentModal(true)
  }

  const executeBulkStatusChange = async (comment: string) => {
    if (!pendingBulkAction) return

    const newStatus = pendingBulkAction
    setShowCommentModal(false)
    setBulkActionInProgress(true)

    try {
      const response = await apiClient.post('/configurationdrift/bulk-status-change', {
        driftIds: Array.from(selectedDrifts),
        newStatus,
        approvedBy: 'Current User',
        comments: comment || `Bulk ${newStatus.toLowerCase()} action`,
      })

      toast.success(response.data.message || `Updated ${selectedDrifts.size} drifts.`)

      if (newStatus === 'Rejected') {
        setRejectedDriftCount(selectedDrifts.size)
        setShowRejectionNotification(true)
      }

      setSelectedDrifts(new Set())
      setShowImpactWarning(false)
      setBulkApprovalImpact(null)
      setPendingBulkAction(null)

      setTimeout(() => {
        onRefresh()
      }, 500)
    } catch (error: any) {
      console.error('Bulk status change error:', error)
      toast.error(error.response?.data?.error || 'Failed to update drift statuses')
    } finally {
      setBulkActionInProgress(false)
      setShowImpactWarning(false)
    }
  }

  /* ────────────────────────────
     Columns, groups, batch bar
     ──────────────────────────── */

  const columns: DataColumn<ConfigurationDrift>[] = [
    {
      id: 'severity',
      header: 'Severity',
      cell: (d) => <SevPill severity={d.severity} />,
      nowrap: true,
    },
    {
      id: 'setting',
      header: 'Setting',
      primary: true,
      width: '34%',
      cell: (d) => {
        // Readable names (drift-names.ts); the raw keys stay in the expanded detail.
        const resource = dscResourceLabel(d.component)
        const name = d.componentName && d.componentName !== d.component ? ` › ${d.componentName}` : ''
        const item = <TableItem title={settingLabel(d.setting)} sub={`${workloadLabelForResourceType(d.component)} · ${resource}${name}`} />
        return (
          <span className="block min-w-0 [overflow-wrap:anywhere]">
            {renderDetail ? <RowToggleTitle onToggle={() => setExpandedIds((ids) => toggleId(ids, d.id))}>{item}</RowToggleTitle> : item}
            <span className="mt-1 block empty:hidden">
              <TriagePill fields={d} />
            </span>
          </span>
        )
      },
    },
    {
      id: 'change',
      header: 'Change',
      cell: (d) => <ChangeCell from={d.oldValue} to={d.newValue} />,
      nowrap: true,
      hideBelow: 'c-md',
    },
    {
      // The status rides here as a tag (mockup 02 has no Status column); the
      // last action is the newest entry of the expanded row's History.
      id: 'when',
      header: 'When',
      cell: (d) => <WhenCell at={d.detectedAt} status={lifecycleTag(d.status, archiveMode)} />,
      nowrap: true,
    },
  ]

  // Resources with several drifted settings (e.g. SPOTenantSettings after a
  // DSC upgrade) roll up under one collapsed group header; a resource with one
  // drifted setting stays a plain row (flatSingles).
  const groups: DataTableGroups<ConfigurationDrift> = {
    key: groupKey,
    label: (_k, rows) => driftResourceName(rows[0].component),
    flatSingles: true,
    expandAll: true,
    header: (_k, rows) => {
      const worst = rows.reduce((a, b) => (severityOrder[b.severity] > severityOrder[a.severity] ? b : a))
      const first = rows[0]
      return (
        <span className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
          <SevPill severity={worst.severity} />
          <span className="min-w-0 [overflow-wrap:anywhere]">
            {driftResourceName(first.component)}
            {first.componentName && first.componentName !== first.component ? (
              <span className="font-normal text-text-secondary"> › {first.componentName}</span>
            ) : null}
          </span>
          <span className="text-helper font-normal tabular-nums text-text-helper">{rows.length} settings drifted</span>
        </span>
      )
    },
  }

  const selectable = !readOnly && !archiveMode
  const batchActions: BatchAction[] = [...PRIMARY_VERBS, ...OVERFLOW_VERBS].map((verb, i) => ({
    id: verb.key,
    label: verb.label,
    shortLabel: verb.shortLabel,
    icon: verb.key === 'approve' ? <IconCheck /> : verb.key === 'reject' ? <IconClose /> : undefined,
    dividerBefore: i === PRIMARY_VERBS.length,
    overflow: i >= PRIMARY_VERBS.length,
    disabled: bulkActionInProgress,
    onClick: () => void handleBulkStatusChange(verb.status),
  }))

  const total = pagination.total
  const severityOptions = severityFilterOptions(total, severityCounts, countsUnknown)
  // Only a never-scanned tenant's unfiltered Open view has no baseline data to
  // show. A filter or the Back to baseline tab narrows a real list, and after a
  // failed stats call the drift list still loaded, so those empty views are real.
  const notAssessed =
    unscanned &&
    !archiveMode &&
    !filters.severity &&
    !filters.status &&
    !filters.fromDate &&
    !filters.toDate &&
    !hiddenStatuses?.awaiting


  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Pending approvals notice */}
      {hasPendingDrifts && !readOnly ? (
        <InlineNotification
          kind="info"
          title={pendingDrifts.length === 1 ? 'One change is waiting to apply.' : `${pendingDrifts.length} changes are waiting to apply.`}
          actions={
            <span className="flex flex-wrap gap-2">
              <Button kind="tertiary" size="sm" onClick={onRefresh} disabled={bulkActionInProgress}>
                Refresh status
              </Button>
              <Button kind="secondary" size="sm" onClick={() => void handleCancelPendingApprovals()} disabled={bulkActionInProgress}>
                Cancel pending
              </Button>
            </span>
          }
        >
          Pending approvals wait for the next batch run. Cancel them to reset them to New if they shouldn&apos;t go through.
        </InlineNotification>
      ) : null}

      {/* Bulk approval impact notice */}
      {showImpactWarning && bulkApprovalImpact ? (
        <InlineNotification
          kind="info"
          title="Resource-level updates will trigger."
          actions={
            <span className="flex flex-wrap gap-2">
              <Button
                kind="secondary"
                size="sm"
                onClick={() => {
                  setShowImpactWarning(false)
                  void handleBulkStatusChange('Approved' as DriftStatus)
                }}
                disabled={bulkActionInProgress}
              >
                Proceed with bulk approval
              </Button>
              <Button
                kind="tertiary"
                size="sm"
                onClick={() => {
                  setShowImpactWarning(false)
                  setBulkApprovalImpact(null)
                }}
              >
                Cancel
              </Button>
            </span>
          }
        >
          <BulkImpactBody impact={bulkApprovalImpact} />
        </InlineNotification>
      ) : null}

      <div id="drift-queue" className="scroll-mt-24">
      <DataTable
        title="Drifts"
        description={
          archiveMode
            ? 'Drifts that are back to your baseline, kept for the record. Bulk actions are off here.'
            : 'Settings that no longer match your baseline.'
        }
        columns={columns}
        rows={drifts}
        rowId={(d) => d.id}
        rowLabel={(d) => `drift ${settingLabel(d.setting)}`}
        tabs={tabs}
        toolbar={
          <>
            <TableSelect
              label="Severity"
              value={filters.severity}
              options={severityOptions}
              onChange={(value) => onFilterChange({ ...filters, severity: value })}
              className="flex-1"
            />
            <DriftRefinePopover filters={filters} onFilterChange={onFilterChange} hidden={hiddenStatuses} />
            {onExport ? <IconButton label="Export audit" icon={<IconDownload />} onClick={onExport} /> : null}
          </>
        }
        selectable={selectable}
        selectedIds={Array.from(selectedDrifts)}
        onSelectionChange={(ids) => setSelectedDrifts(new Set(ids))}
        batchActions={selectable ? batchActions : undefined}
        groups={groups}
        expandable={renderDetail ? { render: renderDetail, expandedIds, onExpandedChange: setExpandedIds } : undefined}
        onRowActivate={renderDetail ? undefined : (d) => onRowClick(d.id)}
        isLoading={isLoading}
        empty={
          // Unknown is never the all-clear: before the first assessment the
          // queue says so, in the Hybrid table's words.
          notAssessed ? (
            <>
              <span className="block text-text-primary">{QUEUE_NOT_ASSESSED.title}</span>
              <span className="mt-1 block text-helper">{QUEUE_NOT_ASSESSED.body}</span>
            </>
          ) : (
            <>
              <span className="block text-text-primary">Nothing to review in this view.</span>
              <span className="mt-1 block text-helper">Adjust the filters or switch tabs to see more.</span>
            </>
          )
        }
        pagination={
          total > 0
            ? {
                page: pagination.current,
                pageSize: pagination.pageSize,
                total,
                onPageChange: pagination.onPageChange,
                itemLabel: archiveMode ? 'remediated drifts' : 'drifts',
                isLoading,
              }
            : undefined
        }
      />
      </div>

      {/* Bulk Action Comment Modal */}
      <BulkActionCommentModal
        isOpen={showCommentModal}
        onClose={() => {
          setShowCommentModal(false)
          setPendingBulkAction(null)
        }}
        onConfirm={(comment) => void executeBulkStatusChange(comment)}
        actionStatus={pendingBulkAction ?? ('Approved' as DriftStatus)}
        selectedCount={selectedDrifts.size}
        isProcessing={bulkActionInProgress}
      />

      {/* Rejection Notification Modal */}
      <RejectionNotificationModal
        isOpen={showRejectionNotification}
        onClose={() => setShowRejectionNotification(false)}
        rejectedCount={rejectedDriftCount}
        isBulkAction={rejectedDriftCount > 1}
      />
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Bulk approval impact body (spans only: it sits inside the notice)
   ──────────────────────────────────────────────────────────────────── */

function BulkImpactBody({ impact }: { impact: any }) {
  const groupedResources = new Map<string, { component: string; componentName: string; settings: Set<string>; allAdditionalDrifts: Map<string, any> }>()
  for (const resource of impact.impactedResources ?? []) {
    const key = `${resource.component}:${resource.componentName}`
    if (!groupedResources.has(key)) {
      groupedResources.set(key, {
        component: resource.component,
        componentName: resource.componentName,
        settings: new Set(),
        allAdditionalDrifts: new Map(),
      })
    }
    const group = groupedResources.get(key)!
    group.settings.add(resource.setting)
    resource.additionalDriftsAffected?.forEach((drift: any) => {
      group.allAdditionalDrifts.set(drift.id, drift)
    })
  }

  return (
    <span className="block">
      <span className="block text-text-primary">{impact.warning}</span>
      {groupedResources.size > 0 ? (
        <span className="mt-3 block">
          <span className="block font-semibold text-text-primary">Resources with complex property additions</span>
          {Array.from(groupedResources.entries()).map(([key, group]) => (
            <span key={key} className="mt-2 block border-l border-line-strong pl-3">
              <span className="block text-text-primary">
                {group.component}: {group.componentName}
              </span>
              <span className="block">Settings: {Array.from(group.settings).join(', ')}</span>
              {group.allAdditionalDrifts.size > 0 ? (
                <span className="mt-1 block">
                  Additional drifts that will be included:
                  {Array.from(group.allAdditionalDrifts.values()).map((drift: any) => (
                    <span key={drift.id} className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="text-text-primary">{drift.setting}</span>
                      <SevPill severity={drift.severity} />
                      <span className="text-helper text-text-helper">{formatStatusLabel(drift.status)}</span>
                    </span>
                  ))}
                </span>
              ) : null}
            </span>
          ))}
        </span>
      ) : null}
      {impact.recommendation ? <span className="mt-2 block">{impact.recommendation}</span> : null}
    </span>
  )
}
